import { ArrowRight, Check, FileSearch, FileText, Rocket, Users } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@/components/shadcn/button';
import { Card, CardContent } from '@/components/shadcn/card';
import { Label } from '@/components/shadcn/label.tsx';
import type { ValidationChange } from '../../types/base-entity';

type DiscoveryOverviewProps = ValidationChange & {
  onBeginDiscovery?: () => void;
};

export default function DiscoveryOverview({ onValidationChange, onBeginDiscovery }: DiscoveryOverviewProps) {
  const navigate = useNavigate();

  useEffect(() => {
    onValidationChange?.(true);
  }, [onValidationChange]);

  const handleBeginDiscovery = () => {
    if (onBeginDiscovery) {
      onBeginDiscovery();
    } else {
      navigate('form');
    }
  };

  return (
    <div className='flex flex-col flex-1 items-center bg-gray-50 px-6 py-10'>
      <div className='w-full max-w-4xl mx-auto'>
        <header className='text-center mb-12'>
          <h1 className='text-4xl font-bold text-gray-900'>Discovery Overview</h1>
          <p className='text-lg text-gray-600 mt-4'>
            Help us understand how your department operates so we can configure SEIN Terminal to fit your work.
          </p>
        </header>

        <Card className='bg-white rounded-xl border border-gray-300 shadow-none mb-10 p-0'>
          <CardContent className='p-6'>
            <Label className='text-lg font-semibold text-gray-900'>What You're Doing</Label>
            <p className='text-base text-gray-500 mt-4 leading-relaxed'>
              You're providing information about how your department currently manages programs, projects, and properties. This helps us
              understand how work actually happens today — across people, systems, and processes.
            </p>
          </CardContent>
        </Card>

        <Card className='bg-white rounded-xl border border-gray-300 shadow-none mb-10 p-6 md:p-8'>
          <CardContent className='p-0'>
            <Label className='text-lg font-semibold text-gray-900'>Why We're Asking</Label>
            <p className='text-base text-gray-500 mt-4 leading-relaxed'>
              We use this information to identify opportunities to consolidate systems, reduce manual work, and configure SEIN Terminal to
              support how you already operate.
            </p>
            <p className='text-base text-gray-500 mt-4 leading-relaxed'>This step helps ensure:</p>
            <ul className='list-disc pl-5 mt-2 space-y-1 text-base text-gray-500 marker:text-gray-900'>
              <li>Faster setup</li>
              <li>Less rework</li>
              <li>A platform that reflects your real workflows</li>
            </ul>
          </CardContent>
        </Card>

        <Card className='bg-white rounded-xl border border-gray-300 shadow-none mb-10 p-6 md:p-8'>
          <CardContent className='p-0'>
            <Label className='text-lg font-semibold text-gray-900'>How Your Information Is Used</Label>
            <ul className='mt-4 space-y-3 text-base text-gray-500'>
              {[
                'Used to scope configuration and onboarding',
                'Used to identify reporting and integration needs',
                'Used to inform pricing and implementation timelines',
                'Not used to evaluate individual performance',
                'Not shared outside your organization without approval',
              ].map((item) => (
                <li key={item} className='flex items-start gap-3'>
                  <Check className='w-5 h-5 text-green-600 shrink-0 mt-0.5' />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card className='bg-white rounded-xl border border-gray-300 shadow-none mb-10 p-6 md:p-8'>
          <CardContent className='p-0'>
            <Label className='text-lg font-semibold text-gray-900'>What to Expect</Label>
            <ul className='list-disc pl-5 mt-4 space-y-2 text-base text-gray-500'>
              <li>Takes approximately 15-25 minutes</li>
              <li>You don't need to know every answer</li>
              <li>You can skip questions you're unsure about</li>
              <li>You can return and complete it later</li>
            </ul>
          </CardContent>
        </Card>

        <Card className='bg-white rounded-xl border border-gray-300 shadow-none mb-10 p-6 md:p-8'>
          <CardContent className='p-0'>
            <Label className='text-lg font-semibold text-gray-900 mb-8'>What Happens Next</Label>
            <div className='grid grid-cols-1 md:grid-cols-4 gap-6'>
              {[
                {
                  icon: <FileSearch className='w-6 h-6 text-green-700' />,
                  step: 'Step 1',
                  active: true,
                  title: 'You share context',
                  desc: 'Submit information about how your department operates today.',
                },
                {
                  icon: <Users className='w-6 h-6 text-gray-500' />,
                  step: 'Step 2',
                  active: false,
                  title: 'We review and scope',
                  desc: 'Our team reviews your responses to identify configuration and consolidation needs.',
                },
                {
                  icon: <FileText className='w-6 h-6 text-gray-500' />,
                  step: 'Step 3',
                  active: false,
                  title: 'You receive a proposal',
                  desc: 'We provide a clear proposal outlining recommended modules, pricing, and a path to go-live.',
                },
                {
                  icon: <Rocket className='w-6 h-6 text-gray-500' />,
                  step: 'Step 4',
                  active: false,
                  title: 'We implement together',
                  desc: 'Once approved, we configure SEIN Terminal and get your team live on a modern, unified platform.',
                },
              ].map(({ icon, step, active, title, desc }) => (
                <div key={step} className='flex flex-col items-center text-center'>
                  <div
                    className={`w-14 h-14 rounded-full bg-gray-200 border-2 ${active ? 'border-green-700' : 'border-gray-300'} flex items-center justify-center mb-3`}
                  >
                    {icon}
                  </div>
                  <span
                    className={`text-xs font-semibold uppercase bg-gray-200 rounded-full px-2 py-1 tracking-wide ${active ? 'text-green-700' : 'text-gray-500'}`}
                  >
                    {step}
                  </span>
                  <Label className='font-semibold text-gray-900 mt-1 text-base'>{title}</Label>
                  <p className='text-sm text-gray-600 mt-2'>{desc}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className='flex flex-col items-center mt-8'>
          <Button
            type='button'
            onClick={handleBeginDiscovery}
            className='bg-green-800 hover:bg-green-900 text-white px-8 py-6 text-base font-medium rounded-lg shadow-none flex items-center gap-2'
          >
            Begin Discovery
            <ArrowRight className='w-5 h-5' />
          </Button>
          <p className='text-gray-500 text-sm mt-4'>Questions about this process?</p>
        </div>

        <div className='flex h-[2px] bg-gray-300 mt-8' />

        <p className='text-center text-gray-600 text-xs mt-10 max-w-3xl mx-auto'>
          Your responses will be combined with other departments to design a unified solution.
          <br />
          All progress is automatically saved.
        </p>
      </div>
    </div>
  );
}
